import {Component, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {MovimentiService} from '../../../_services/movimenti.service';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {SaveMessage} from '../../../_helpers/save-message';
import {Contenitore} from '../../../_models/Contenitore';
import {SearchResult} from '../../../_models/SearchResult';
import {IonInput} from '@ionic/angular';
import {AuthenticationService} from "../../../_services/authentication.service";
import {HttpErrorResponse} from '@angular/common/http';
import {ContenitoreFormato} from '../../../_models/ContenitoreFormato';
import {validaCodiceSuFormati} from '../../../_helpers/codice-contenitore-formato';
import {KeyboardWedgeBurst} from '../../../_helpers/keyboard-wedge-burst';
import {SbloccoEffimero} from '../../../_helpers/sblocco-effimero';
import {ModalitaModificaContenitore} from '../../../_models/AccountOrganizzazione';

const MESSAGGIO_ERRORE_CARICAMENTO = 'Ci sono problemi con il caricamento del contenitore.';

// Messaggio del server nel corpo di un BadRequest ({ error: "…" }), se presente.
function messaggioServer(err: unknown): string | undefined {
  const corpo = (err as HttpErrorResponse | undefined)?.error as { error?: unknown } | null | undefined;
  return typeof corpo?.error === 'string' && corpo.error ? corpo.error : undefined;
}

@Component({
    selector: 'app-box-input',
    templateUrl: './box-input.component.html',
    styleUrls: ['./box-input.component.scss'],
    standalone: false
})
export class BoxInputComponent  implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('codBoxInput') codBoxInput!: IonInput;
  @Input() codBox = '';
  @Output() codBoxChange = new EventEmitter<string>();
  @Input() codBoxBinding = true;
  @Input() tipoBox = 0;
  @Output() tipoBoxChange = new EventEmitter<number>(); 
  @Input() formato = 0;
  @Output() formatoChange = new EventEmitter<number>();
  @Input() tara = 0;
  @Output() taraChange = new EventEmitter<number>();
  @Input() rifOdP = '';
  @Input() idAnagrafica = 0;
  @Input() codArt = '';
  @Input() desArt = '';
  @Input() udM = '';
  @Input() udM2 = '';
  @Input() codLottoAlfa = '';
  @Input() codeDate: Date;
  @Input() contesto = 0;
  @Output() giacenzaOfBox = new EventEmitter<Contenitore>();
  @Input() showCreate = false;
  @Input() showSelect = true; 
  @Input() generaOnInit = false;
  @Input() onlyAvailable = false;
  @Input() showLotto = true;
  @Input() activeFocus = false;
  @Input() activeFormato = false;
  @Input() readonly = false;
  @Input() contenitoreFilter: ((c: Contenitore) => boolean) | null = null;
  // Feature 162: legge solo contenitori esistenti, senza il get-or-create di CheckContenitore.
  @Input() soloEsistenti = false;
  contenitore: Contenitore;
  giacenze: Contenitore[] = [];
  showGiacenza = false;
  loading = false;
  isValid = 0;
  // Modalità di immissione dall'ultimo commit (issue #3594): la validazione del codice si applica solo
  // alla digitazione a mano, non alle letture da scanner né ai valori impostati dall'applicazione.
  readonly burst = new KeyboardWedgeBurst();
  // Sorgente di tempo isolata per testabilità (come BadgeReaderService).
  now: () => number = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
  /** Sblocco della modalità ConSblocco (feature 165): si richiude al blur. */
  readonly sblocco = new SbloccoEffimero();
  constructor(
    private authService: AuthenticationService,
    private organizzazioneService: OrganizzazioniService,
    private movimentiService: MovimentiService,
    private saveMessage: SaveMessage) { }

  /**
   * Modalità configurata per l'operatore (feature 165). Letta qui e non passata da fuori: così
   * nessuno dei chiamanti di app-box-input deve cambiare (come per app-lotto-input).
   */
  get modalita(): ModalitaModificaContenitore {
    return this.organizzazioneService.tipoModificaContenitore ?? ModalitaModificaContenitore.Libero;
  }

  /**
   * Fuori dall'opzione: Info contenitore (soloEsistenti) non ha né + né elenco e non crea nulla;
   * un campo già readonly per scelta della schermata resta com'è.
   */
  get esente(): boolean {
    return this.soloEsistenti || this.readonly;
  }

  /**
   * Digitazione (e quindi scansione) bloccata. + ed elenco non guardano questo getter: restano
   * attivi in tutte le modalità, perché sono il modo di impostare il contenitore senza scriverlo.
   */
  get inputBloccato(): boolean {
    if (this.readonly) return true;
    if (this.esente) return false;
    if (this.modalita === ModalitaModificaContenitore.Fisso) return true;
    return this.modalita === ModalitaModificaContenitore.ConSblocco && !this.sblocco.sbloccato;
  }

  get showLucchetto(): boolean {
    return !this.esente && this.modalita === ModalitaModificaContenitore.ConSblocco;
  }

  get placeholderBox(): string {
    return this.inputBloccato && !this.readonly ? "Usa + o l'elenco" : 'Scansiona barcode';
  }

  toggleBoxLock(): void {
    // I tasti premuti a campo chiuso non devono pesare sulla classificazione della lettura (#3594).
    this.burst.reset();
    // Il fuoco va nel campo appena si apre: altrimenti servirebbe un secondo tap.
    this.sblocco.alterna(() => this.codBoxInput?.setFocus());
  }

  onBoxBlur(): void {
    if (this.showLucchetto) {
      this.sblocco.richiudiAlBlur();
    }
  }

  ngOnDestroy(): void {
    this.sblocco.dispose();
  }

  ngOnInit() {
    //this.codBoxInput.setFocus();
    // Prefetch non bloccante dei formati per la validazione del codice digitato (issue #3594).
    void this.formatiOrganizzazione();
    if (this.generaOnInit){
      this.createContenitore();
    }
  }

  // Un codBox impostato dalla schermata è un valore da codice: azzera il rilevatore.
  ngOnChanges(changes: SimpleChanges) {
    if (changes['codBox']) {
      this.burst.reset();
    }
  }

  registraTasto(event: KeyboardEvent) {
    if (this.inputBloccato) return;
    this.burst.registraTasto(event.key, this.now(), event.ctrlKey || event.metaKey);
  }

  registraInput(event: Event) {
    if (this.inputBloccato) return;
    if (event instanceof InputEvent) {
      this.burst.registraInput(event.inputType, event.data);
    }
  }

  ngAfterViewInit() {
    if (this.activeFocus) {
      setTimeout(() => {
        if (this.codBoxInput) {
          this.codBoxInput.setFocus();
        }
      }, 100);  
    }
  }

  // Issue #3594: un codice digitato a mano che non rispetta alcun formato viene bloccato prima
  // dell'invio; il server valida comunque (validaCodice) i codici che avvelenerebbero il contatore.
  getGiacenza = async () => {
    this.isValid = 0;
    const codice = this.codBox;
    const modalita = this.burst.classifica(codice);
    // Il riconoscimento riparte da zero a ogni commit, anche nei rami di rifiuto.
    this.burst.reset();
    if (!codice) {
      return false;
    }
    if (this.soloEsistenti) {
      return this.leggiContenitoreEsistente(codice);
    }
    if (modalita === 'manuale' && !(await this.codiceManualeValido(codice))) {
      return false;
    }
    return this.inviaCheckContenitore(codice);
  };

  // Formati non caricabili → validazione saltata (resta la protezione del server).
  private async codiceManualeValido(codice: string): Promise<boolean> {
    const formati = await this.formatiOrganizzazione();
    if (!formati) return true;
    const formatoCampo = formati.find(f => f.id === this.formato) ?? null;
    const esito = validaCodiceSuFormati(codice, formati, formatoCampo);
    if (esito.valido) return true;
    this.isValid = -1;
    this.saveMessage.messageError(esito.messaggio);
    return false;
  }

  private formatiOrganizzazione(): Promise<ContenitoreFormato[] | null> {
    return this.movimentiService.getContenitoriFormatiCachedAsync(this.organizzazioneService.organizzazioneId)
      .catch(() => null);
  }

  private inviaCheckContenitore(codice: string) {
    this.loading = true;
    return this.movimentiService
      .checkContenitoreAsync(this.organizzazioneService.organizzazioneId, codice,
        this.formato ? this.formato : 0, true)
      .then((_giacenza: Contenitore) => this.onGiacenza(_giacenza))
      .catch((err: unknown) => {
        this.isValid = -1;
        this.saveMessage.messageError(messaggioServer(err) ?? MESSAGGIO_ERRORE_CARICAMENTO);
      })
      .finally(() => this.loading = false);
  }

  // Nessuna creazione: un codice sconosciuto è solo «non trovato» (nessun contatore da proteggere).
  private leggiContenitoreEsistente(codice: string) {
    this.loading = true;
    return this.movimentiService
      .getContenitoreAsync(this.organizzazioneService.organizzazioneId, codice)
      .then((_contenitore: Contenitore) => this.onGiacenza(_contenitore))
      .catch(() => {
        this.isValid = -1;
        this.saveMessage.messageError('Contenitore non trovato.');
      })
      .finally(() => this.loading = false);
  }

  private onGiacenza(_giacenza: Contenitore) {
    if ((!this.codArt && _giacenza.codArt) || (_giacenza.codArt === this.codArt)) {
      this.isValid = 1;
    }
    this.contenitore = _giacenza;
    this.giacenzaOfBox.emit(_giacenza);
    if (!this.codBoxBinding) {
      this.codBox = '';
    }
    this.codBoxChange.emit(this.codBox);
  }

  getContenitori = () => {
    this.showGiacenza = !this.showGiacenza;
    if (!this.showGiacenza) {
      this.giacenze = [];
      return false;
    }
    this.loading = true;
    this.movimentiService.getContenitoriFilteredAsync(this.organizzazioneService.organizzazioneId,
      '',0,0,'codice',true, -1,this.codArt, this.rifOdP, '','', '', true)
      .then((_contenitori: SearchResult<Contenitore>) => {
        if (_contenitori.totalCount > 0) {
          _contenitori.result.forEach((_contenitore: Contenitore) => {
            if ((!this.onlyAvailable || _contenitore.giacenza > 0) &&
              (this.formato === 0 || _contenitore.formato === this.formato) &&
              (!this.contenitoreFilter || this.contenitoreFilter(_contenitore))) {
              this.giacenze.push(_contenitore);
            }
          });
        }
      })
      .catch(() => this.saveMessage.messageError("Ci sono problemi con il caricamento delle giacenze."))
      .finally(() => this.loading = false);
  };

  selectContenitore(contenitore: Contenitore) {
    if (!contenitore || !contenitore.codice) {
      return false;
    }
    this.showGiacenza = false;
    this.contenitore = contenitore;
    this.giacenze = [];
    this.giacenzaOfBox.emit(contenitore);
    if (!this.codBoxBinding)
    {
      this.codBox = '';
    } else {
      this.codBox = contenitore.codice;
    }
    // Valore scelto dalla lista: l'ionChange al blur non deve validarlo.
    this.burst.reset();
    this.codBoxChange.emit(this.codBox);
  };

  createContenitore = () => {
    this.contenitore =  {
      id: 0,
      organizzazioneId: this.organizzazioneService.organizzazioneId,
      formato: this.formato,
      tipoBox: this.tipoBox,
      codice: '',
      descrizione: this.desArt,
      codArt: this.codArt,
      udM: this.udM,
      udM2: this.udM2,
      magUdM: this.udM,
      codLottoAlfa: this.codLottoAlfa,
      rifOdP: this.rifOdP,
      idAnagrafica: this.idAnagrafica,
      codeDate: this.codeDate,
      contesto: this.contesto,
      executedBy: this.authService.currentUserValue.userName,
      isActive: true
    };
    this.loading = true;
    this.movimentiService.createContenitoreAsync(this.contenitore)
      .then((_contenitore: Contenitore) => {
        this.contenitore = _contenitore;
        this.codBoxChange.emit(this.contenitore.codice);
        this.isValid = 1;
      })
      .catch(() => this.saveMessage.messageError("Ci sono problemi con la creazione del contenitore."))
      .finally(() => this.loading = false);
  };
  
  updateFormato = (formatoId: number) => {
    this.formatoChange.emit(formatoId);
    if (this.contenitore) {
      this.contenitore.formato = formatoId;  
    }
  }
  updateTipo = (tipo) => this.tipoBox = tipo;
  
  updateContenitore = () => {
    this.loading = true;
    this.movimentiService.updateContenitoreAsync(this.contenitore)
      .then((_contenitore: Contenitore) => {
        this.contenitore = _contenitore;
        this.codBoxChange.emit(this.contenitore.codice);
        this.isValid = 1;
        if (_contenitore.tara) {
          this.taraChange.emit(_contenitore.tara);
        }
      })
      .catch(() => this.saveMessage.messageError("Ci sono problemi con l'aggiornamento del contenitore."))
      .finally(() => this.loading = false);
  };
}
